import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { closeMaterialFormAction } from '../effects/handleCloseMaterialForm'
import { bulkUpdateAutomatedObject } from '../../../api/takeoff-api'
import { AIAutomatedObject } from '../../../models/aiClassifications'
import { Project } from '../../../models/project'
import { markAiSuggestionsDeleted } from '../../../utils/aiSuggestions/aiParser'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import {
    clearSelectedAiSuggestionIds,
    deleteSelectedAiSuggestionId,
    selectAiSuggestions,
    selectSelectedPaperIdToDelete,
    setAiSuggestions,
    setSelectedPaperIdToDelete,
} from '../../slices/2D'
import { selectProject } from '../../slices/project'

export interface DeleteAISuggestionPayload {
    aiSuggestionIds: string[]
    reason?: string
}

export const deleteAISuggestionAction = createAction<DeleteAISuggestionPayload>('deleteAISuggestionAction')

export function* handleDeleteAISuggestion({ payload }: ReturnType<typeof deleteAISuggestionAction>) {
    if (!payload) return

    const { aiSuggestionIds, reason } = payload

    try {
        const manager: PaperManager = yield call(managers.get2DManager)

        if (!manager) return

        const [selectTool, workspaceTool]: [Select, Workspace] = yield call(manager.getTools, [
            Select.NAME,
            Workspace.NAME,
        ])

        const paperItemsId: number | null = yield select(selectSelectedPaperIdToDelete)

        if (aiSuggestionIds === null && paperItemsId === null) return

        let aiSuggestionIdsToDelete = aiSuggestionIds

        if (paperItemsId !== null) {
            const itemsWithAltClick: paper.Path[] = yield call(workspaceTool.getItemsWithCriteria, 'id', function (id) {
                return paperItemsId === id
            })

            aiSuggestionIdsToDelete = itemsWithAltClick.reduce((acc, item) => {
                const id = item.data?.aiSuggestion?.id

                if (id) acc.push(id)

                return acc
            }, [] as string[])
        }

        if (aiSuggestionIdsToDelete.length === 0) return

        const aiItems: paper.Path[] = yield call(workspaceTool.getItemsWithCriteria, 'data', function (data) {
            return aiSuggestionIdsToDelete.includes(data?.aiSuggestion?.id)
        })

        const aiDataArray: AIAutomatedObject[] = yield select(selectAiSuggestions)
        const updatedAiData: AIAutomatedObject[] = yield markAiSuggestionsDeleted(aiDataArray, aiSuggestionIdsToDelete)

        const project: Project = yield select(selectProject)

        yield call(bulkUpdateAutomatedObject, project.id, aiSuggestionIdsToDelete, reason)

        yield put(setAiSuggestions(updatedAiData))

        for (const selectedAISuggestionID of aiSuggestionIdsToDelete) {
            yield put(deleteSelectedAiSuggestionId(selectedAISuggestionID))
        }

        yield all(aiItems.map((item) => call(item.remove.bind(item))))

        yield put(closeMaterialFormAction())

        yield call(selectTool.exitSelectMode)

        yield put(setSelectedPaperIdToDelete(null))

        yield put(clearSelectedAiSuggestionIds())
    } catch (e) {
        console.error('Error on deleting updating AI', e)
    }
}
export function* watchForDeleteAISuggestion() {
    yield takeLatest([deleteAISuggestionAction.type, setSelectedPaperIdToDelete.type], handleDeleteAISuggestion)
}
